"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  ScheduleCanvasAppointmentInput,
  ScheduledCanvasAppointmentResponse,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface ScheduleCanvasAppointmentVariables {
  input: ScheduleCanvasAppointmentInput;
}

interface ScheduleCanvasAppointmentResponse {
  data?: {
    scheduleCanvasAppointment: ScheduledCanvasAppointmentResponse;
  };
}

export const scheduleCanvasAppointmentMutation = gql`
  mutation scheduleCanvasAppointment($input: ScheduleCanvasAppointmentInput!) {
    scheduleCanvasAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  ScheduleCanvasAppointmentVariables,
  {
    scheduleCanvasAppointment: ScheduledCanvasAppointmentResponse;
  }
>;

export const useScheduleCanvasAppointment = (
  options: MutationOptions<
    ScheduleCanvasAppointmentResponse,
    Error,
    ScheduleCanvasAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    ScheduleCanvasAppointmentResponse,
    Error,
    ScheduleCanvasAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(scheduleCanvasAppointmentMutation, variables),
  });

  const scheduleCanvasAppointment = async (
    input: ScheduleCanvasAppointmentInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      return response?.data?.scheduleCanvasAppointment;
    } catch (error) {
      console.error("Failed to schedule Canvas appointment", error);
      throw error;
    }
  };

  return {
    scheduleCanvasAppointment,
    isPending,
  };
};
